import { memo, useMemo } from 'react'
import { Toggle, Select, Button } from '../atoms'
import { DEFAULT_RADIUS_OPTIONS } from '../../utils/constants'
import type { AppSettings } from '../../types'

export interface SettingsPanelProps {
  settings: AppSettings
  onSettingsChange: (settings: Partial<AppSettings>) => void
  onClearPOIs?: () => void
  onRefreshPOIs?: () => void
  onClearPinnedLocations?: () => void
  poiCount?: number
  pinnedCount?: number
}

export const SettingsPanel = memo(({
  settings,
  onSettingsChange,
  onClearPOIs,
  onRefreshPOIs,
  onClearPinnedLocations,
  poiCount = 0,
  pinnedCount = 0,
}: SettingsPanelProps) => {
  const radiusOptions = useMemo(
    () =>
      DEFAULT_RADIUS_OPTIONS.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    []
  )

  return (
    <div className="space-y-2">
      <Toggle
        checked={settings.radio}
        onChange={(checked) => {
          onSettingsChange({ radio: checked })
          // Stop current speaker when disabling auto-narration
          if (!checked) {
            try {
              window.speechSynthesis.cancel()
            } catch (_) {
              // Ignore errors
            }
          }
        }}
        label="Auto-Narration"
        description="Automatically narrate new points of interest as you discover them"
      />
      <Toggle
        checked={settings.autoUpdate}
        onChange={(checked) => onSettingsChange({ autoUpdate: checked })}
        label="Auto-Update Location"
        description="Automatically refresh your location every 2 minutes to discover new POIs"
      />
      <Toggle
        checked={settings.showPOIs}
        onChange={(checked) => onSettingsChange({ showPOIs: checked })}
        label="Show POIs on Map"
        description="Display point of interest markers on the map"
      />
      <Toggle
        checked={settings.useGoogle}
        onChange={(checked) => onSettingsChange({ useGoogle: checked })}
        label="Google Places"
        description="Use Google Places API for additional points of interest data (requires API key)"
      />
      <div className="pt-2 border-t border-border">
        <label className="block text-sm font-medium text-text mb-2">
          Search Radius
        </label>
        <Select
          value={String(settings.radius)}
          onChange={(e) =>
            onSettingsChange({ radius: Number(e.target.value) })
          }
          options={radiusOptions}
          aria-label="Search radius"
          className="w-full"
        />
      </div>

      {/* Map Actions */}
      <div className="pt-3 border-t border-border space-y-2">
        <label className="block text-sm font-medium text-text mb-2">
          Map Actions
        </label>
        
        {onRefreshPOIs && (
          <Button
            onClick={onRefreshPOIs}
            variant="secondary"
            size="sm"
            className="w-full text-xs"
          >
            🔄 Refresh POIs
          </Button>
        )}
        
        {onClearPOIs && poiCount > 0 && (
          <Button
            onClick={onClearPOIs}
            variant="secondary"
            size="sm"
            className="w-full text-xs"
          >
            🗑️ Clear POIs ({poiCount})
          </Button>
        )}
        
        {onClearPinnedLocations && pinnedCount > 0 && (
          <Button
            onClick={onClearPinnedLocations}
            variant="secondary"
            size="sm"
            className="w-full text-xs"
          >
            📍 Clear Pins ({pinnedCount})
          </Button>
        )}
      </div>
    </div>
  )
})

SettingsPanel.displayName = 'SettingsPanel'
